import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ExternalLink, ImageOff, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Pagination, PaginationContent, PaginationItem } from '@/components/ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { SocialAnalyticsMediaQuery } from '@/generated/graphql';
import { formatAnalyticsSyncDate } from '@/lib/social-analytics/formatAnalyticsSyncDate';
import { formatMetric, formatSignedMetric } from '@/lib/social-analytics/formatMetric';
import { mediaTypeKey } from '@/lib/social-analytics/mediaTypeKey';
import { safeHttpUrl } from '@/lib/social-analytics/safeHttpUrl';

export type MediaRow = SocialAnalyticsMediaQuery['socialAnalyticsMedia']['data'][number];
type SortDirection = 'asc' | 'desc';
type PaginationInfo = { page: number; page_size: number; page_count: number; total: number };
type SortProps = { sort: string; direction: SortDirection; onSortChange: (sort: string, direction: SortDirection) => void };
type AllContentTableProps = SortProps & {
  data: readonly MediaRow[];
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  onOpenDetails?: (row: MediaRow) => void;
  emptyState?: ReactNode;
};

const SortableHead = ({ field, label, sort, direction, onSortChange }: SortProps & { field: string; label: string }) => {
  const active = sort === field;
  const nextDirection: SortDirection = active && direction === 'asc' ? 'desc' : 'asc';
  const Icon = !active ? ArrowUpDown : direction === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}>
      <Button variant="ghost" size="sm" className="-ml-2 h-8 px-2 text-xs font-medium text-muted-foreground" onClick={() => onSortChange(field, nextDirection)}>
        {label}
        <Icon className="ml-1 h-3.5 w-3.5" />
      </Button>
    </TableHead>
  );
};

export const AllContentTable = ({ data, pagination, sort, direction, onSortChange, onPageChange, onOpenDetails, emptyState }: AllContentTableProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('pt') ? 'pt-BR' : 'en-US';
  const fallback = '—';
  const sortProps = { sort, direction, onSortChange };
  return (
    <section aria-labelledby="all-content-title" className="overflow-hidden rounded-xl border border-border/70 bg-card">
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-3 sm:px-5">
        <div>
          <h2 id="all-content-title" className="text-base font-semibold">{t('analytics.content.title')}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('analytics.content.resultCount', { count: pagination.total })}</p>
        </div>
      </div>
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('analytics.content.columns.content')}</TableHead>
              <TableHead>{t('analytics.content.columns.account')}</TableHead>
              <SortableHead field="views" label={t('analytics.content.columns.views')} {...sortProps} />
              <SortableHead field="views_gained" label={t('analytics.content.columns.viewsInPeriod')} {...sortProps} />
              <SortableHead field="likes" label={t('analytics.metrics.likes')} {...sortProps} />
              <SortableHead field="comments" label={t('analytics.metrics.comments')} {...sortProps} />
              <SortableHead field="shares" label={t('analytics.metrics.shares')} {...sortProps} />
              <SortableHead field="published_at" label={t('analytics.content.columns.published')} {...sortProps} />
              <SortableHead field="last_synced_at" label={t('analytics.content.columns.lastSync')} {...sortProps} />
              <TableHead><span className="sr-only">{t('analytics.content.details')}</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item) => {
              const href = safeHttpUrl(item.canonical_url);
              const thumbnail = safeHttpUrl(item.thumbnail_url);
              const title = item.title || item.caption || t('analytics.content.untitled');
              return (
                <TableRow key={item.id}>
                  <TableCell className="max-w-[280px]">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
                        {thumbnail
                          ? <img src={thumbnail} alt="" className="h-full w-full object-cover" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
                          : <ImageOff className="h-3.5 w-3.5 text-muted-foreground" />}
                      </div>
                      <div className="min-w-0">
                        {href
                          ? <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 truncate font-medium hover:underline">{title}<ExternalLink className="h-3 w-3 shrink-0" /></a>
                          : <span className="block truncate font-medium">{title}</span>}
                        <p className="truncate text-xs text-muted-foreground">{t(mediaTypeKey(item.media_type))}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">@{item.account?.username || fallback}</div>
                    <div className="text-xs text-muted-foreground">{t(`analytics.platforms.${item.platform}`)}</div>
                  </TableCell>
                  <TableCell className="tabular-nums">{formatMetric(item.views ?? null, locale)}</TableCell>
                  <TableCell className="tabular-nums">{formatSignedMetric(item.views_gained ?? null, locale)}</TableCell>
                  <TableCell className="tabular-nums">{formatMetric(item.likes ?? null, locale)}</TableCell>
                  <TableCell className="tabular-nums">{formatMetric(item.comments ?? null, locale)}</TableCell>
                  <TableCell className="tabular-nums">{formatMetric(item.shares ?? null, locale)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{formatAnalyticsSyncDate(item.published_at, locale, fallback)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{formatAnalyticsSyncDate(item.last_synced_at, locale, fallback)}</TableCell>
                  <TableCell className="w-10">
                    {onOpenDetails && (
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label={t('analytics.content.detailsFor', { title })} onClick={() => onOpenDetails(item)}>
                        <Info className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {data.length === 0
        ? (emptyState ?? <p className="px-5 py-12 text-center text-sm text-muted-foreground">{t('analytics.content.noContent')}</p>)
        : <div className="lg:hidden"><div className="px-3 py-3"><span className="sr-only">{t('analytics.content.mobileEquivalent')}</span></div></div>}
      <div className="border-t border-border/60 px-4 py-3">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <Button type="button" variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => onPageChange(pagination.page - 1)}>{t('analytics.pagination.previous')}</Button>
            </PaginationItem>
            <PaginationItem>
              <span className="px-2 text-xs tabular-nums text-muted-foreground">{t('analytics.pagination.page', { page: pagination.page, count: Math.max(1, pagination.page_count) })}</span>
            </PaginationItem>
            <PaginationItem>
              <Button type="button" variant="outline" size="sm" disabled={pagination.page >= pagination.page_count} onClick={() => onPageChange(pagination.page + 1)}>{t('analytics.pagination.next')}</Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </section>
  );
};
